import React, { useState, useEffect, useRef } from 'react';
import { Spinner, Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './admin.css';
import webservice from '../../../services/webservice';
import swal from 'sweetalert2';
import { FaTimes } from "react-icons/fa";
import { FaSort } from "react-icons/fa6";
import { FaSortDown, FaSortUp } from 'react-icons/fa';
import { GrDatabase } from "react-icons/gr";
import Select from 'react-select';

const statusOptions = [
    { value: 'All', label: 'All Status', color: '#6e7687', bg: '#f4f6f8' },
    { value: 'Active', label: 'Active', color: '#10b981', bg: '#d1fae5' },
    { value: 'Blocked', label: 'Blocked', color: '#ef4444', bg: '#fee2e2' },
];

const customSelectStyles = {
    control: (provided, state) => {
        const selected = state.hasValue ? state.getValue()[0] : null;
        return {
            ...provided,
            width: '150px',
            minHeight: '32px',
            height: '32px',
            borderRadius: '20px',
            backgroundColor: selected ? selected.bg : '#f4f6f8',
            borderColor: selected && selected.value !== 'All' ? selected.color : '#e2e8f0',
            boxShadow: 'none',
            cursor: 'pointer',
            '&:hover': {
                borderColor: selected && selected.value !== 'All' ? selected.color : '#cbd5e1'
            }
        };
    },
    valueContainer: (provided) => ({
        ...provided,
        height: '32px',
        padding: '0 10px'
    }),
    input: (provided) => ({
        ...provided,
        margin: '0px',
        padding: '0px'
    }),
    indicatorSeparator: () => ({
        display: 'none'
    }),
    indicatorsContainer: (provided, state) => {
        const selected = state.hasValue ? state.getValue()[0] : null;
        return {
            ...provided,
            height: '32px',
            color: selected ? selected.color : '#6e7687'
        };
    },
    dropdownIndicator: (provided, state) => {
        const selected = state.hasValue ? state.getValue()[0] : null;
        return {
            ...provided,
            color: selected ? selected.color : '#6e7687',
            padding: '4px 8px',
            '&:hover': {
                color: selected ? selected.color : '#333'
            }
        };
    },
    option: (provided, state) => ({
        ...provided,
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        backgroundColor: state.isSelected ? state.data.bg : state.isFocused ? '#f8f9fa' : '#fff',
        color: state.data.color,
        cursor: 'pointer',
        fontWeight: '600',
        fontSize: '12px'
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: '12px',
        overflow: 'hidden',
        zIndex: 9999,
        marginTop: '4px',
        width: '150px'
    }),
    singleValue: (provided, state) => ({
        ...provided,
        color: state.data.color,
        fontWeight: '600',
        fontSize: '12px',
        margin: '0px'
    })
};

const formatStatusLabel = ({ label, color }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ height: '8px', width: '8px', backgroundColor: color, borderRadius: '50%', display: 'inline-block', marginRight: '8px' }}></span>
        {label}
    </div>
);

const AdminCreation = () => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const [filters, setFilters] = useState({
        search: '',
        status: 'All'
    });
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const searchTimeoutRef = useRef(null);
    const [sortBy, setSortBy] = useState('createdAt:desc');

    // Pagination state
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);


    const fetchAdmins = async () => {
        try {
            setLoading(true);
            const [sortField, sortOrder] = sortBy.split(':');
            const response = await axios.get(`${webservice}api/admin-list`, {
                params: { 
                    page, 
                    limit: pageSize,
                    search: debouncedSearch,
                    status: filters.status,
                    sortBy: sortField,
                    sortOrder: sortOrder
                }
            });
            if (response.data.success) {
                setAdmins(response.data.data);
                setTotalCount(response.data.totalRecords || 0);
                setTotalPages(response.data.totalPages || 0);
            }
        } catch (error) {
            console.error('Error fetching admins:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdmins();
    }, [page, pageSize, debouncedSearch, filters.status, sortBy]);

    const handleBlockUnblock = async (id, currentIsBlocked) => {
        const actionText = currentIsBlocked ? "unblock" : "block";
        swal.fire({
            title: 'Are you sure?',
            text: `Do you want to ${actionText} this admin account?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: `Yes, ${actionText} it!`
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await axios.post(`${webservice}api/update-admin-status`, {
                        id,
                        isBlocked: !currentIsBlocked
                    });
                    if (response.data.success) {
                        swal.fire(
                            `${currentIsBlocked ? 'Unblocked!' : 'Blocked!'}`,
                            `Admin has been ${actionText}ed successfully.`,
                            'success'
                        );
                        fetchAdmins();
                    }
                } catch (error) {
                    console.error('Error updating status:', error);
                    swal.fire('Error', 'Something went wrong.', 'error');
                }
            }
        });
    };

    const handleDelete = async (id) => {
        swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await axios.post(`${webservice}api/delete-admin`, { id });
                    if (response.data.success) {
                        swal.fire(
                            'Deleted!',
                            'Admin account has been deleted.',
                            'success'
                        );
                        fetchAdmins();
                    }
                } catch (error) {
                    console.error('Error deleting admin:', error);
                    swal.fire('Error', 'Something went wrong while deleting.', 'error');
                }
            }
        });
    };

    const startIndex = (page - 1) * pageSize + 1;
    const endIndex = Math.min(page * pageSize, totalCount);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(1);
    };

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setFilters(prev => ({ ...prev, search: val }));

        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(val);
            setPage(1);
        }, 500);
    };

    const clearFilters = () => {
        setFilters({ search: '', status: 'All' });
        setDebouncedSearch('');
        setPage(1);
    };

    const handleSortChange = (field) => {
        const currentField = sortBy.split(':')[0];
        const currentOrder = sortBy.split(':')[1];
        
        if (currentField === field) {
            setSortBy(`${field}:${currentOrder === 'asc' ? 'desc' : 'asc'}`);
        } else {
            setSortBy(`${field}:asc`);
        }
    };

    const renderSortIcon = (field) => {
        const [currentField, currentOrder] = sortBy.split(':');
        if (currentField !== field) return <FaSort />;
        return currentOrder === 'asc' ? <FaSortUp /> : <FaSortDown />;
    };

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                    <h2>Admins</h2>
                    <p>Manage administrator accounts and access</p>
                </div>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                        onClick={() => navigate("/admin-creation/add")}
                        className="admin-btn admin-btn-primary"
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        + Add Admin
                    </button>
                </div>
            </div>

                <div style={{ background: '#fff', padding: '10px 15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', border: '1px solid #eaeaea' }}>
                    <Form.Control
                        type="text"
                        placeholder="Search Name, Mobile, Role..."
                        value={filters.search}
                        onChange={handleSearchChange}
                        style={{ width: '250px', height: '32px', fontSize: '13px', borderRadius: '20px', backgroundColor: '#f4f6f8', border: '1px solid #e2e8f0', color: '#333' }}
                    />
                    <Select
                        options={statusOptions}
                        value={statusOptions.find(opt => opt.value === filters.status) || statusOptions[0]}
                        onChange={(selectedOption) => handleFilterChange('status', selectedOption.value)}
                        styles={customSelectStyles}
                        formatOptionLabel={formatStatusLabel}
                        isSearchable={false}
                    />
                    {(filters.search || filters.status !== 'All') && (
                        <button
                            onClick={clearFilters}
                            style={{
                                height: '32px', padding: '0 15px', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '20px', 
                                fontWeight: 600, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer'
                            }}
                        >
                            <FaTimes /> Clear
                        </button>
                    )}
                </div>
            <div style={{ height: '60vh', overflowY: 'auto', backgroundColor: '#fff', border: '1px solid #eaeaea', borderRadius: '8px 8px 0 0', marginTop: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                <table className="admin-table" style={{ width: '100%', margin: 0, borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        <tr>
                            <th style={{ padding: '15px 20px', fontWeight: '600', color: '#000', backgroundColor: '#fff', borderBottom: 'none' }}>S.NO</th>
                                    <th onClick={() => handleSortChange('name')} style={{ cursor: 'pointer', padding: '15px 20px', fontWeight: '600', color: '#000', backgroundColor: '#fff', borderBottom: 'none', whiteSpace: 'nowrap' }}>
                                        Name <span style={{ fontSize: '12px', color: '#999', marginLeft: '5px' }}>{renderSortIcon('name')}</span>
                                    </th>
                                    <th onClick={() => handleSortChange('mobile')} style={{ cursor: 'pointer', padding: '15px 20px', fontWeight: '600', color: '#000', backgroundColor: '#fff', borderBottom: 'none', whiteSpace: 'nowrap' }}>
                                        Mobile <span style={{ fontSize: '12px', color: '#999', marginLeft: '5px' }}>{renderSortIcon('mobile')}</span>
                                    </th>
                                    <th onClick={() => handleSortChange('userType')} style={{ cursor: 'pointer', padding: '15px 20px', fontWeight: '600', color: '#000', backgroundColor: '#fff', borderBottom: 'none', whiteSpace: 'nowrap' }}>
                                        Role <span style={{ fontSize: '12px', color: '#999', marginLeft: '5px' }}>{renderSortIcon('userType')}</span>
                                    </th>
                                    <th onClick={() => handleSortChange('isBlocked')} style={{ cursor: 'pointer', padding: '15px 20px', fontWeight: '600', color: '#000', backgroundColor: '#fff', borderBottom: 'none', whiteSpace: 'nowrap' }}>
                                        Status <span style={{ fontSize: '12px', color: '#999', marginLeft: '5px' }}>{renderSortIcon('isBlocked')}</span>
                                    </th>
                            <th style={{ padding: '15px 20px', fontWeight: '600', color: '#000', backgroundColor: '#fff', borderBottom: 'none' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr style={{ border: "none" }}>
                                <td colSpan="6" className="text-center" style={{ border: "none", height: "53vh" }}>
                                    <Spinner animation="border" variant="primary" />
                                </td>
                            </tr>
                        ) : admins.length > 0 ? (
                            admins.map((admin, index) => (
                                <tr key={admin._id} style={{ borderBottom: '1px solid #eaeaea' }}>
                                    <td style={{ padding: '12px 20px' }}>{startIndex + index}</td>
                                    <td style={{ padding: '12px 20px' }}>{admin.name}</td>
                                    <td style={{ padding: '12px 20px' }}>{admin.mobile}</td>
                                    <td style={{ padding: '12px 20px' }}>{admin.userType}</td>
                                    <td style={{ padding: '12px 20px' }}>
                                        <span className={`status-badge ${admin.isBlocked ? 'status-blocked' : 'bg-success'}`} style={{ padding: '6px 10px', borderRadius: '4px', display: 'inline-block', color:"#fff"}}>
                                            {admin.isBlocked ? 'Blocked' : 'Active'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px 20px' }}>
                                        <div className="action-btns">
                                            <Link to={`/admin-creation/edit/${admin._id}`}>
                                                <button className="action-btn btn-edit">Edit</button>
                                            </Link>
                                            <button
                                                className={`action-btn ${admin.isBlocked ? 'btn-unblock' : 'btn-block-action'}`}
                                                onClick={() => handleBlockUnblock(admin._id, admin.isBlocked)}
                                            >
                                                {admin.isBlocked ? 'Unblock' : 'Block'}
                                            </button>
                                            <button
                                                className="action-btn btn-delete"
                                                onClick={() => handleDelete(admin._id)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" style={{ textAlign: 'center', height: '54vh', verticalAlign: 'middle', color: '#777' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                        <GrDatabase fontSize={40} color="#bbb" style={{ border: "none", }} />
                                        <span>No data found.</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="pagination-music" style={{ padding: '15px 20px', border: '1px solid #eaeaea', borderTop: 'none', borderRadius: '0 0 8px 8px', backgroundColor: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div className="pagination-info" style={{ color: '#000', fontWeight: '500' }}>
                    Showing {totalCount > 0 ? startIndex : 0} - {endIndex} of {totalCount} records
                </div>
                <div className="pagination-buttons" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                        className="page-btn"
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                        style={{ padding: '6px 10px', border: 'none', backgroundColor: 'transparent', color: page === 1 ? '#ccc' : '#555', fontWeight: '600', cursor: page === 1 ? 'not-allowed' : 'pointer' }}
                    >
                        Prev
                    </button>

                    {(() => {
                        let pages = [];
                        if (totalPages <= 5) {
                            for (let i = 1; i <= totalPages; i++) pages.push(i);
                        } else {
                            if (page <= 3) pages = [1, 2, 3, '...', totalPages];
                            else if (page >= totalPages - 2) pages = [1, '...', totalPages - 2, totalPages - 1, totalPages];
                            else pages = [1, '...', page - 1, page, page + 1, '...', totalPages];
                        }
                        return pages.map((p, index) => (
                            <button
                                key={index}
                                className={`page-btn ${page === p ? 'active' : ''}`}
                                onClick={() => p !== '...' && setPage(p)}
                                disabled={p === '...'}
                                style={{
                                    padding: '4px 5px',
                                    border: p === '...' ? 'none' : (page === p ? '1px solid #3b82f6' : '1px solid #eaeaea'),
                                    borderRadius: '8px',
                                    backgroundColor: p === '...' ? 'transparent' : (page === p ? '#3b82f6' : '#fff'),
                                    color: page === p ? '#fff' : '#555',
                                    fontWeight: page === p ? '600' : '500',
                                    cursor: p === '...' ? 'default' : 'pointer',
                                    minWidth: '25px'
                                }}
                            >
                                {p}
                            </button>
                        ));
                    })()}

                    <button
                        className="page-btn"
                        onClick={() => setPage(page + 1)}
                        disabled={page === totalPages || totalPages === 0}
                        style={{ padding: '6px 10px', border: 'none', backgroundColor: 'transparent', color: page === totalPages || totalPages === 0 ? '#ccc' : '#555', fontWeight: '600', cursor: page === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer' }}
                    >
                        Next
                    </button>
                </div>
            </div>
        </>
    );
};

export default AdminCreation;
